import React from 'react';
import { CellWithTooltip } from '@/components/DataView/EditableTable';
import { convertCurrency } from '@/helpers/convertCurrency';
import { Planogram } from '@/store/Planogram/findAll';
import { ColumnsType } from 'antd/lib/table';
import { BarcodeOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import {
    StatusLocked,
    StatusUnlocked,
    OldValue,
    NewValue,
    WrapperValue,
    ProductInfo,
    BarCodeArea,
    ProductTitle,
    ProductBarCode,
    BlurredCell,
    StoreName,
    PercentVariation,
} from './styled';
import { PriceCalcOptions } from '../PlanogramAdjust';
import { getPriceChange } from './utils';

export type PlanogramWithChanges = Planogram & {
    price_final_in_cents_after: number;
    price_list_in_cents_after: number;
    status_after: string;
    alert_level: number;
    alert_level_after: number;
    standard_amount_after?: number;
    enabled_to_calculate_margin?: boolean;
    price_calc_option: keyof typeof PriceCalcOptions;
    delete?: boolean;
};

function renderPriceCell(before: number, after: number, isInvalid: boolean) {
    const sameValue = before === after;

    const priceChange = getPriceChange(before, after);

    const renderValue = (value: number) => (
        <BlurredCell isInvalid={isInvalid}>
            <CellWithTooltip
                valueCell={convertCurrency('', value)}
                ptbrCurrency
            />
        </BlurredCell>
    );

    return (
        <WrapperValue style={{ display: 'flex', gap: 6 }}>
            {sameValue ? (
                <NewValue>{renderValue(before)}</NewValue>
            ) : (
                <>
                    <OldValue>{renderValue(before)}</OldValue>
                    <NewValue>
                        {renderValue(after)}{' '}
                        <PercentVariation
                            style={{
                                color: priceChange.isIncrease
                                    ? '#52c41a'
                                    : '#f5232d',
                            }}
                        >
                            ({priceChange.formattedPtBr})
                        </PercentVariation>
                    </NewValue>
                </>
            )}
        </WrapperValue>
    );
}

export const columns: ColumnsType<PlanogramWithChanges> = [
    {
        title: 'Produto',
        dataIndex: 'name',
        width: 150,
        ellipsis: { showTitle: true },
        render: (_, record) => {
            const isInvalid =
                !record.enabled_to_calculate_margin &&
                record.price_calc_option === PriceCalcOptions.categoryMargin &&
                !record.delete;

            return (
                <BlurredCell isInvalid={isInvalid}>
                    <ProductInfo>
                        <Tooltip
                            title={record.product.name}
                            open={isInvalid ? false : undefined}
                        >
                            <ProductTitle>{record.product.name}</ProductTitle>
                        </Tooltip>
                        <BarCodeArea>
                            <BarcodeOutlined
                                style={{ marginRight: 8, color: '#B0B0B0' }}
                            />
                            <ProductBarCode>
                                {record.product.BarCodes[0]?.code ||
                                    'Nenhum código de barras cadastrado'}
                            </ProductBarCode>
                        </BarCodeArea>
                        <StoreName>{record.store.name}</StoreName>
                    </ProductInfo>
                </BlurredCell>
            );
        },
    },
    {
        title: 'Qnt. padrão',
        width: 120,
        render: (_, record) => {
            const isInvalid =
                !record.enabled_to_calculate_margin &&
                record.price_calc_option === PriceCalcOptions.categoryMargin &&
                !record.delete;

            if (record.standard_amount === record.standard_amount_after) {
                return (
                    <BlurredCell isInvalid={isInvalid}>
                        <NewValue>{record.standard_amount}</NewValue>
                    </BlurredCell>
                );
            }
            return (
                <BlurredCell isInvalid={isInvalid}>
                    <WrapperValue>
                        <OldValue>{record.standard_amount}</OldValue>
                        <NewValue>{record.standard_amount_after}</NewValue>
                    </WrapperValue>
                </BlurredCell>
            );
        },
    },
    {
        title: 'Nível de alerta',
        width: 140,
        render: (_, record: PlanogramWithChanges) => {
            const isInvalid =
                !record.enabled_to_calculate_margin &&
                record.price_calc_option === PriceCalcOptions.categoryMargin &&
                !record.delete;

            if (record.alert_level === record.alert_level_after) {
                return (
                    <BlurredCell isInvalid={isInvalid}>
                        <NewValue>{record.alert_level_after}</NewValue>
                    </BlurredCell>
                );
            }

            return (
                <BlurredCell isInvalid={isInvalid}>
                    <WrapperValue>
                        <OldValue>{record.alert_level}</OldValue>
                        <NewValue>{record.alert_level_after}</NewValue>
                    </WrapperValue>
                </BlurredCell>
            );
        },
    },
    {
        title: 'Preço lista',
        width: 150,
        render: (_, record: PlanogramWithChanges) => {
            const isInvalid =
                !record.enabled_to_calculate_margin &&
                record.price_calc_option === PriceCalcOptions.categoryMargin &&
                !record.delete;

            return renderPriceCell(
                record.price_list_in_cents,
                record.price_list_in_cents_after,
                isInvalid,
            );
        },
    },
    {
        title: 'Preço App',
        width: 150,
        render: (_, record: PlanogramWithChanges) => {
            const isInvalid =
                !record.enabled_to_calculate_margin &&
                record.price_calc_option === PriceCalcOptions.categoryMargin &&
                !record.delete;

            return renderPriceCell(
                record.price_final_in_cents,
                record.price_final_in_cents_after,
                isInvalid,
            );
        },
    },
    {
        width: 243,
        title: 'Status',
        render: (_, record: PlanogramWithChanges) => {
            const status = record.status_after;

            const isInvalid =
                !record.enabled_to_calculate_margin &&
                record.price_calc_option === PriceCalcOptions.categoryMargin &&
                !record.delete;

            if (status === 'lock') {
                return (
                    <BlurredCell isInvalid={isInvalid}>
                        <StatusLocked>
                            Bloqueado para abastecimento
                        </StatusLocked>
                    </BlurredCell>
                );
            }

            if (status === 'lockSold') {
                return (
                    <BlurredCell isInvalid={isInvalid}>
                        <StatusLocked>Bloqueado para venda</StatusLocked>
                    </BlurredCell>
                );
            }

            return (
                <BlurredCell isInvalid={isInvalid}>
                    <StatusUnlocked>Desbloqueado</StatusUnlocked>
                </BlurredCell>
            );
        },
    },
];
